import { useMemo, useState } from 'react'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useCollection } from '../../lib/firestore'
import { formatDay } from '../../lib/format'
import { resolvedAccessLevel } from '../../types/models'
import type { Flight, Hotel, Tour, TourCrew, WithId } from '../../types/models'
import Icon from '../../components/Icon'
import Empty from '../../components/Empty'
import DeleteConfirm from '../../components/DeleteConfirm'
import CrewDialog from '../../dialogs/CrewDialog'
import { removeCrewMember, resendInvitation } from '../../lib/mutations'
import SearchField from '../../components/SearchField'

/**
 * The touring party.
 *
 * A crew list isn't an address book — the questions a tour manager actually
 * asks it are "who's on this tour", "who still hasn't accepted", "what's this
 * person's number", "which flight are they on" and "who can change things".
 * Each of those is answerable here without leaving the page.
 */

type Filter = 'all' | 'editors' | 'viewers' | 'pending'

export default function CrewTab({
  tour,
  canManage,
}: {
  tour: WithId<Tour>
  canManage: boolean
}) {
  const { user, uid } = useAuth()
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])
  const flights = useCollection<Flight>('flights', [where('tourId', '==', tour.id)])
  const hotels = useCollection<Hotel>('hotels', [where('tourId', '==', tour.id)])

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<WithId<TourCrew> | null>(null)
  const [deleting, setDeleting] = useState<WithId<TourCrew> | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const counts = useMemo(() => {
    let editors = 0
    let viewers = 0
    let pending = 0
    for (const member of crew.data) {
      if (resolvedAccessLevel(member) === 'editor') editors += 1
      else viewers += 1
      if (member.status !== 'accepted') pending += 1
    }
    return { all: crew.data.length, editors, viewers, pending }
  }, [crew.data])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return crew.data
      .filter((member) => {
        if (filter === 'editors' && resolvedAccessLevel(member) !== 'editor') return false
        if (filter === 'viewers' && resolvedAccessLevel(member) !== 'viewer') return false
        if (filter === 'pending' && member.status === 'accepted') return false
        if (!needle) return true
        return (
          member.name.toLowerCase().includes(needle) ||
          (member.email ?? '').toLowerCase().includes(needle) ||
          (member.roles ?? []).some((role) => role.toLowerCase().includes(needle))
        )
      })
      // Anyone who hasn't accepted floats up — they're the ones needing action.
      .sort((a, b) => {
        const aPending = a.status === 'accepted' ? 1 : 0
        const bPending = b.status === 'accepted' ? 1 : 0
        if (aPending !== bPending) return aPending - bPending
        return a.name.localeCompare(b.name)
      })
  }, [crew.data, query, filter])

  const inviter = {
    id: uid ?? '',
    name: user?.displayName || user?.email || 'Someone',
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="spread" style={{ gap: 14, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {(
            [
              ['all', 'Everyone', counts.all],
              ['editors', 'Editors', counts.editors],
              ['viewers', 'Viewers', counts.viewers],
              ['pending', 'Not accepted', counts.pending],
            ] as const
          ).map(([value, label, count]) => {
            const selected = filter === value
            return (
              <button
                key={value}
                onClick={() => setFilter(value)}
                aria-pressed={selected}
                className="row"
                style={{
                  gap: 7,
                  padding: '7px 13px',
                  borderRadius: 'var(--radius-chip)',
                  fontSize: '0.85rem',
                  fontWeight: selected ? 600 : 400,
                  background: 'var(--panel)',
                  border: `${selected ? '2px' : '1px'} solid ${
                    selected ? 'var(--accent)' : 'var(--hairline)'
                  }`,
                  color: 'var(--text-primary)',
                }}
              >
                {label}
                <span className="t-spec-caption faint">{count}</span>
              </button>
            )
          })}
        </div>

        <div className="row" style={{ gap: 10 }}>
          <SearchField
            label="Search name, role or email"
            value={query}
            onChange={setQuery}
            width={220}
          />
          {canManage && (
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              <Icon name="plus" size={16} /> Add crew
            </button>
          )}
        </div>
      </div>

      {crew.data.length === 0 && !crew.loading && (
        <Empty
          title="Nobody on this tour yet"
          message="Add the people travelling and invite them in. Editors change the plan, viewers follow it."
          action={canManage ? { label: 'Add someone', onClick: () => setAdding(true) } : undefined}
        />
      )}

      {crew.data.length > 0 && visible.length === 0 && (
        <p className="panel panel-pad t-caption">Nobody matches that.</p>
      )}

      {visible.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 14,
            alignItems: 'start',
          }}
        >
          {visible.map((member) => (
            <CrewMemberCard
              key={member.id}
              member={member}
              flights={flights.data}
              hotels={hotels.data}
              open={openId === member.id}
              onToggle={() => setOpenId(openId === member.id ? null : member.id)}
              canManage={canManage}
              onEdit={() => setEditing(member)}
              onDelete={() => setDeleting(member)}
              inviter={inviter}
              tour={tour}
            />
          ))}
        </div>
      )}

      {(adding || editing) && user && (
        <CrewDialog
          open
          member={editing}
          tour={tour}
          inviter={inviter}
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
        />
      )}

      {deleting && (
        <DeleteConfirm
          label={deleting.name}
          detail={`They lose access to ${tour.tourName} immediately. Their flights and hotel rooms stay on the tour but stop showing a name.`}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await removeCrewMember(deleting)
            setDeleting(null)
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------- card */

/**
 * One crew member, expanding to everything they are on.
 *
 * Exported because the tour Overview shows the same rows and must behave the
 * same way when clicked — a second, flatter crew card there would be one more
 * thing to keep in step.
 */
export function CrewMemberCard({
  member,
  flights,
  hotels,
  open,
  onToggle,
  canManage,
  onEdit,
  onDelete,
  inviter,
  tour,
}: {
  member: WithId<TourCrew>
  flights: WithId<Flight>[]
  hotels: WithId<Hotel>[]
  open: boolean
  onToggle: () => void
  canManage: boolean
  onEdit: () => void
  onDelete: () => void
  inviter: { id: string; name: string }
  tour: WithId<Tour>
}) {
  const level = resolvedAccessLevel(member)
  const accepted = member.status === 'accepted'

  // Resend state lives on the card. Every row can be mid-send independently,
  // and a reissued code has to appear on the row it belongs to.
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState<{ joiningCode: string | null; existing: boolean } | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)

  async function resend() {
    setSending(true)
    setSendError(null)
    try {
      setSent(await resendInvitation(member, tour, inviter))
    } catch (caught) {
      setSendError((caught as Error).message)
    } finally {
      setSending(false)
    }
  }

  // What this person is actually on. The single most useful thing a TM opens a
  // crew record for, and it can't be answered from the crew document alone.
  const theirFlights = flights.filter((flight) =>
    flight.passengers?.some((passenger) => passenger.crewId === member.id),
  )
  const theirRooms = hotels.flatMap((hotel) =>
    hotel.rooms
      .filter((room) => room.guests.some((guest) => guest.crewId === member.id))
      .map((room) => ({ hotel, room })),
  )

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="row"
        style={{ width: '100%', padding: 16, gap: 13, textAlign: 'left', alignItems: 'flex-start' }}
      >
        <Avatar name={member.name} />

        <span className="grow stack" style={{ gap: 3, minWidth: 0 }}>
          <span className="truncate" style={{ fontWeight: 600 }}>
            {member.name}
          </span>
          <span className="t-caption truncate">
            {member.roles?.join(' · ') || 'No role set'}
          </span>
          <span className="row" style={{ gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
            <span className={level === 'editor' ? 'chip chip-accent' : 'chip'}>{level}</span>
            {!accepted && <span className="chip chip-warning">{member.status}</span>}
          </span>
        </span>

        <Icon
          name={open ? 'chevronDown' : 'chevronRight'}
          size={15}
          style={{ color: 'var(--text-tertiary)', marginTop: 4 }}
        />
      </button>

      {open && (
        <div
          className="stack"
          style={{
            gap: 14,
            padding: 16,
            background: 'var(--panel-raised)',
            borderTop: '1px solid var(--hairline)',
          }}
        >
          {(member.email || member.phone) && (
            <div className="stack" style={{ gap: 7 }}>
              <span className="t-eyebrow">Contact</span>
              {member.email && (
                <a className="row accent" style={{ gap: 8 }} href={`mailto:${member.email}`}>
                  <Icon name="account" size={15} />
                  <span className="truncate">{member.email}</span>
                </a>
              )}
              {member.phone && (
                <a className="row accent" style={{ gap: 8 }} href={`tel:${member.phone}`}>
                  <Icon name="people" size={15} />
                  <span>{member.phone}</span>
                </a>
              )}
            </div>
          )}

          <div className="stack" style={{ gap: 3 }}>
            <span className="t-eyebrow">Access</span>
            <span className="t-caption">
              {level === 'editor'
                ? 'Can change anything on this tour.'
                : "Sees the tour and everything they're on, but can't change it."}
            </span>
            <span className="t-spec-caption faint">
              {member.startDate || member.endDate
                ? `${member.startDate ? formatDay(member.startDate) : 'Start of tour'} – ${
                    member.endDate ? formatDay(member.endDate) : 'End of tour'
                  }`
                : 'Whole tour'}
            </span>
          </div>

          {theirFlights.length > 0 && (
            <div className="stack" style={{ gap: 5 }}>
              <span className="t-eyebrow">On these flights</span>
              {theirFlights.map((flight) => {
                const baggage = flight.passengers.find(
                  (passenger) => passenger.crewId === member.id,
                )?.baggage
                return (
                  <div key={flight.id} className="spread">
                    <span className="t-spec-caption">
                      {flight.origin} → {flight.destination}
                    </span>
                    <span className="t-spec-caption faint">
                      {formatDay(flight.departureTimeUTC)}
                      {baggage ? ` · ${baggage} kg` : ''}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {theirRooms.length > 0 && (
            <div className="stack" style={{ gap: 5 }}>
              <span className="t-eyebrow">Rooms</span>
              {theirRooms.map(({ hotel, room }) => (
                <div key={`${hotel.id}-${room.id}`} className="spread">
                  <span className="t-caption truncate">{hotel.name}</span>
                  <span className="t-spec-caption faint">
                    {room.roomNumber || 'Unassigned'} · {formatDay(hotel.checkInDate, hotel.timezone)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {!accepted && member.invitationCode && (
            <div
              className="stack"
              style={{
                gap: 6,
                padding: '12px 14px',
                borderRadius: 'var(--radius-control)',
                background: 'var(--accent-wash)',
              }}
            >
              <span className="t-eyebrow">Joining code</span>
              <div className="spread">
                <span
                  className="t-spec"
                  style={{ fontSize: '1.3rem', letterSpacing: 4, color: 'var(--accent-text)' }}
                >
                  {member.invitationCode}
                </span>
                <CopyButton value={member.invitationCode} />
              </div>
              <span className="t-caption">
                They create an account with {member.email || 'their email'} and enter this code.
              </span>
            </div>
          )}

          {canManage && !accepted && (
            <div className="stack" style={{ gap: 6 }}>
              <button className="btn btn-secondary" onClick={resend} disabled={sending}>
                {sending ? 'Sending…' : sent ? 'Send again' : 'Resend invite'}
              </button>
              {sent && (
                <span className="t-caption">
                  {sent.existing
                    ? `Notified in the app and emailed to ${member.email || 'their address'}.`
                    : member.email
                      ? `New code ${sent.joiningCode} emailed to ${member.email}.`
                      : `New code ${sent.joiningCode} — no email on file, so read it to them.`}
                </span>
              )}
              {sendError && (
                <span className="t-caption" style={{ color: 'var(--danger-text)' }}>
                  {sendError}
                </span>
              )}
            </div>
          )}

          {canManage && (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onEdit}>
                Edit
              </button>
              <button className="btn btn-danger" style={{ flex: 1 }} onClick={onDelete}>
                Remove
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Initials on a tinted disc. Nobody uploads a crew photo, so this is the mark. */
function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <span
      aria-hidden
      style={{
        display: 'grid',
        placeItems: 'center',
        width: 38,
        height: 38,
        flex: 'none',
        borderRadius: '50%',
        background: 'var(--accent-wash)',
        color: 'var(--accent-text)',
        fontWeight: 700,
        fontSize: '0.8rem',
      }}
    >
      {initials || '—'}
    </span>
  )
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      className="btn btn-quiet"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1800)
        } catch {
          // Clipboard is blocked in some contexts; the code is on screen to
          // read out either way, so this fails quietly rather than alarming.
        }
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}
